import { EmptyState } from "@circle-react-uikit/EmptyState";
import { useNavigate } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useIsConnectPopoverPanel } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isMemberDirectoryVisible } from "@/react/helpers/communityMemberHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useMyConnectionsInfiniteQuery } from "@/react/hooks/myConnections/useMyConnectionsInfiniteQuery";
import { useActiveView } from "@/react/hooks/utils/useActiveView";
import { useThemeContext } from "@/react/providers/Theme";

import { CommunityMembersV2Skeleton } from "../../shared/MembersList/CommunityMembers/CommunityMembersV2Skeleton";
import { EMPTY_MEMBER_IMAGES } from "../../shared/MembersList/CommunityMembers/constants";
import type { Views } from "../../shared/MembersList/constants";
import { VIEWS } from "../../shared/MembersList/constants";
import MembersView from "../../shared/MembersList/MembersView/MembersView";
import { ProgressiveImage } from "../../shared/ProgressiveImage";
import type { MemberDirectorySettings } from "../constants";

export const MyConnections = ({
  memberDirectorySettings,
}: {
  memberDirectorySettings: MemberDirectorySettings;
}) => {
  const isConnectPopoverPanel = useIsConnectPopoverPanel();
  const defaultCommunityView = isConnectPopoverPanel
    ? VIEWS.LIST
    : memberDirectorySettings.view;

  const [activeView] = useActiveView({ defaultCommunityView, views: VIEWS });
  const { currentAppearance } = useThemeContext();
  const navigate = useNavigate();

  const connectionsQuery = useMyConnectionsInfiniteQuery({
    perPage: 10,
  });

  const hasNoConnections =
    connectionsQuery.totalCount === 0 && !connectionsQuery.isPending;

  const { currentCommunityMember, currentCommunitySettings } =
    usePunditUserContext();

  const shouldShowCtaOnEmptyState = isMemberDirectoryVisible(
    currentCommunityMember,
    currentCommunitySettings,
  );

  if (connectionsQuery.isPending) {
    return <CommunityMembersV2Skeleton view={VIEWS.LIST} />;
  }

  const emptyMembersImageVariants = EMPTY_MEMBER_IMAGES[currentAppearance];

  if (hasNoConnections) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="mt-14 flex flex-1 items-center justify-center">
          <ProgressiveImage
            className="h-36"
            src={emptyMembersImageVariants.large}
            srcVariants={emptyMembersImageVariants}
            alt={t(
              "members_directory.recommendations.empty_list.build_connection_header",
            )}
            imageClassName="object-contain"
          />
        </div>
        <EmptyState
          header={t(
            "members_directory.recommendations.empty_list.build_connection_header",
          )}
          description={t(
            "members_directory.recommendations.empty_list.build_connection_description",
          )}
          primaryLabel={
            shouldShowCtaOnEmptyState
              ? t("members_directory.recommendations.empty_list.button")
              : undefined
          }
          onPrimaryClick={() => {
            void navigate({ to: "/members" });
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "flex flex-col gap-6 px-6 pb-20 sm:px-0",
        // In the bounded Connect popover the page provides no bottom spacing,
        // so add padding on desktop to stop the last row butting the edge.
        isConnectPopoverPanel ? "md:pb-8" : "md:pb-0",
      )}
    >
      {!isConnectPopoverPanel && (
        <div className="border-primary hidden items-center justify-between md:flex">
          <div className="flex items-center gap-x-2 pl-2 md:pl-0">
            <Typography.LabelLg weight="semibold" size="text-lg">
              {t("members_directory.connections.title")}
            </Typography.LabelLg>

            {connectionsQuery.totalCount > 0 && (
              <span className="bg-secondary text-dark flex h-[22px] items-center justify-center rounded-3xl px-1.5 py-0.5 text-center text-xs font-medium">
                {connectionsQuery.totalCount}
              </span>
            )}
          </div>
        </div>
      )}

      <MembersView
        activeView={activeView as Views}
        fetchNextPage={connectionsQuery.fetchNextPage}
        hasNextPage={connectionsQuery.hasNextPage ?? false}
        members={connectionsQuery.members}
        shouldShowHiddenMemberTooltip={false}
      />
    </div>
  );
};
